import React, { useState } from 'react';
import { X, Video, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';
import { directoryApi } from '../../../lib/api';

const inputCls =
  'w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#C7FA3E] focus:outline-none transition-colors';

const Overlay = ({ children, onClose, testId }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid={testId}>
    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full max-w-md bg-[#0d0d0f] border border-zinc-800 p-6 max-h-[90vh] overflow-y-auto">
      {children}
    </div>
  </div>
);

const ModalShell = ({ icon: Icon, title, subtitle, onClose, children, done, doneMsg }) => (
  <>
    <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors" data-testid="dfw-modal-close" aria-label="Close">
      <X className="w-5 h-5" />
    </button>
    {done ? (
      <div className="text-center py-8" data-testid="dfw-modal-success">
        <CheckCircle2 className="w-10 h-10 text-[#C7FA3E] mx-auto mb-4" />
        <p className="text-white font-semibold mb-1">Submitted!</p>
        <p className="text-zinc-400 text-sm">{doneMsg}</p>
      </div>
    ) : (
      <>
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-4 h-4 text-[#C7FA3E]" />
          <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.03em' }}>{title}</h3>
        </div>
        <p className="text-zinc-500 text-xs mb-5">{subtitle}</p>
        {children}
      </>
    )}
  </>
);

export const VideoSubmitModal = ({ gym, onClose }) => {
  const [form, setForm] = useState({ video_url: '', title: '', submitter_name: '', submitter_email: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await directoryApi.submitVideo(gym.gym_id, form);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Submission failed — please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClose={onClose} testId="dfw-video-modal">
      <ModalShell
        icon={Video}
        title={`Add a video for ${gym.name}`}
        subtitle="Paste a YouTube, Vimeo or Instagram link. Our team reviews every video before it goes live."
        onClose={onClose}
        done={done}
        doneMsg="Your video will appear on this listing once approved."
      >
        <form onSubmit={submit} className="space-y-3">
          <input required type="url" placeholder="https://youtube.com/watch?v=… *" value={form.video_url} onChange={set('video_url')} className={inputCls} data-testid="dfw-video-url-input" />
          <input type="text" placeholder="Video title (optional)" value={form.title} onChange={set('title')} className={inputCls} data-testid="dfw-video-title-input" />
          <input required type="text" placeholder="Your name *" value={form.submitter_name} onChange={set('submitter_name')} className={inputCls} data-testid="dfw-video-name-input" />
          <input required type="email" placeholder="Your email *" value={form.submitter_email} onChange={set('submitter_email')} className={inputCls} data-testid="dfw-video-email-input" />
          {error && <p className="text-[#E63946] text-xs" data-testid="dfw-video-error">{error}</p>}
          <button type="submit" disabled={loading} className="btn-ninja w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="dfw-video-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />} Submit Video
          </button>
        </form>
      </ModalShell>
    </Overlay>
  );
};

export const ClaimListingModal = ({ gym, onClose }) => {
  const [form, setForm] = useState({ contact_name: '', contact_email: '', phone: '', buy_class_url: '', pricing_text: '', video_url: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await directoryApi.claimListing(gym.gym_id, form);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Submission failed — please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClose={onClose} testId="dfw-claim-modal">
      <ModalShell
        icon={ShieldCheck}
        title={`Claim ${gym.name}`}
        subtitle="Own or manage this gym? Add your class-purchase link, pricing and a video. We verify every claim before updating the listing."
        onClose={onClose}
        done={done}
        doneMsg="Our team will verify your claim and update the listing."
      >
        <form onSubmit={submit} className="space-y-3">
          <input required type="text" placeholder="Your name *" value={form.contact_name} onChange={set('contact_name')} className={inputCls} data-testid="dfw-claim-name-input" />
          <input required type="email" placeholder="Business email *" value={form.contact_email} onChange={set('contact_email')} className={inputCls} data-testid="dfw-claim-email-input" />
          <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={set('phone')} className={inputCls} data-testid="dfw-claim-phone-input" />
          <input type="url" placeholder="Direct link to buy a class (https://…)" value={form.buy_class_url} onChange={set('buy_class_url')} className={inputCls} data-testid="dfw-claim-buylink-input" />
          <input type="text" placeholder="Pricing — e.g. $25 drop-in · $189/mo" value={form.pricing_text} onChange={set('pricing_text')} className={inputCls} data-testid="dfw-claim-pricing-input" />
          <input type="url" placeholder="Video link — YouTube / Instagram (optional)" value={form.video_url} onChange={set('video_url')} className={inputCls} data-testid="dfw-claim-video-input" />
          <textarea rows={2} placeholder="Anything else? (optional)" value={form.notes} onChange={set('notes')} className={inputCls} data-testid="dfw-claim-notes-input" />
          {error && <p className="text-[#E63946] text-xs" data-testid="dfw-claim-error">{error}</p>}
          <button type="submit" disabled={loading} className="btn-ninja w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="dfw-claim-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Claim This Listing
          </button>
        </form>
      </ModalShell>
    </Overlay>
  );
};
